/**
 * One CSRF transport for legacy destructive actions. The server endpoint is
 * same-origin, session-bound, and not cached. Token is not embedded in HTML.
 */
export default class DestructiveRequest {
    constructor({fetcher = (...args) => fetch(...args), documentRef = (typeof document !== 'undefined' ? document : null)} = {}) {
        this.fetcher = fetcher;
        this.documentRef = documentRef;
        this.token = null;
        this.loading = null;
    }

    sameOrigin(url) {
        const parsed = new URL(url, this.documentRef.location?.href || window.location.href);
        if (parsed.origin !== (this.documentRef.location?.origin || window.location.origin)) {
            throw new Error('Cross-origin destructive request refused');
        }
        return parsed.href;
    }

    async csrf() {
        if (this.token) return this.token;
        if (!this.loading) {
            const config = JSON.parse(this.documentRef.getElementById('app-security-config')?.textContent || '{}');
            if (!config.csrfUrl) throw new Error('Security endpoint is not configured');
            const endpoint = this.sameOrigin(config.csrfUrl);
            this.loading = this.fetcher(endpoint, {
                method: 'GET', credentials: 'same-origin', cache: 'no-store',
                headers: {'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest'},
            }).then(async (response) => {
                if (!response.ok) throw new Error(`Security token unavailable: ${response.status}`);
                const payload = await response.json();
                if (typeof payload.token !== 'string' || !payload.token) throw new Error('Invalid security token');
                this.token = payload.token;
                return this.token;
            }).finally(() => {this.loading = null;});
        }
        return this.loading;
    }

    async post(url, {as = 'json'} = {}) {
        const target = this.sameOrigin(url);
        const token = await this.csrf();
        const response = await this.fetcher(target, {
            method: 'POST', credentials: 'same-origin', cache: 'no-store',
            headers: {
                'X-CSRF-Token': token,
                'X-Requested-With': 'XMLHttpRequest',
                'Accept': as === 'json' ? 'application/json' : 'text/html',
            },
        });
        if (response.status === 403) this.token = null;
        if (!response.ok) throw new Error(`Destructive operation failed: ${response.status}`);
        return response;
    }

    async submit(url) {
        const target = this.sameOrigin(url);
        const token = await this.csrf();
        const form = this.documentRef.createElement('form');
        form.method = 'POST';
        form.action = target;
        form.hidden = true;
        const input = this.documentRef.createElement('input');
        input.type = 'hidden';
        input.name = '_csrf_token';
        input.value = token;
        form.append(input);
        this.documentRef.body.append(form);
        form.submit(); // navigation, not an AJAX request
    }
}
